import { useState } from 'preact/hooks';
import type { StripeCheckoutSession } from '@stripe/stripe-js';
import type { CampaignPromo, OnsiteProduct, OnsitePrice, CheckoutError, CheckoutCompletionResponse, CheckoutSuccessDetails } from '@/types';
import { CheckoutShell } from '@/components/CheckoutShell';
import { OrderSummary } from '@/components/OrderSummary';
import { PaymentForm } from '@/components/PaymentForm';
import { t, useT } from '@/i18n/t';

interface Props {
  // The catalog product/price behind the checkout. Absent only transiently in
  // direct checkout before price-config resolves (or when it fails); the order
  // summary then falls back to a neutral label and the live session's totals.
  product?: OnsiteProduct;
  price?: OnsitePrice;
  amountCentsOverride?: number;
  clientSecret: string;
  publishableKey: string;
  checkoutId: string;
  connectedAccount?: string;
  // The domain behind the checkout; the promo apply/display uses it to
  // translate between the typed code and its namespaced Stripe-side string.
  domainId?: number;
  // Server-masked checkout email (ezAuth path only); display-only.
  maskedEmail?: string;
  // Renders a "Not you?" switch next to the identity note. Absent hides it
  // (guests, and BYO domains whose adapter has no logout).
  onSwitchIdentity?: () => void;
  // Whether this session accepts a promo code (the domain has an active promo
  // code and this is not a donation). Drives the promo-code entry's visibility.
  promotionCodesEnabled?: boolean;
  // See CheckoutResponse.savePaymentMethod.
  savePaymentMethod?: boolean;
  // Mounts the Express Checkout Element as the wallet surface above the card
  // form (the Payment Element's own wallet buttons are suppressed).
  showExpressCheckout?: boolean;
  // False hides every wallet surface (see CheckoutFlow walletsEnabled).
  walletsEnabled?: boolean;
  // Fired once when the Express Checkout Element reports ready; the flow
  // beacons it as the wallets_ready timing marker.
  onWalletsReady?: () => void;
  // Fired when the visitor applies or removes a promo code (null on removal),
  // so the owning flow can attach it to later funnel events.
  onPromoChange?: (code: string | null) => void;
  // The mount's campaign code, auto-applied by the form (see
  // PaymentForm.campaignPromo).
  campaignPromo?: CampaignPromo | null;
  // Fired when the campaign code's auto-apply succeeds.
  onCampaignApply?: () => void;
  // Fired when the campaign code's auto-apply fails, with the machine reason
  // and, when EWCS rejected the apply, Stripe's raw error text as detail.
  onCampaignApplyFailed?: (reason: string, detail?: string) => void;
  // Payment confirmed and access provisioned; carries the backend completion
  // (whose email the success screen reads on the deferred-identity paths) and
  // the final session (whose totals the success screen renders).
  onSuccess: (completion: CheckoutCompletionResponse, details?: CheckoutSuccessDetails) => void;
  // Reports a failed checkout attempt to the publisher. The step stays open for
  // retry, so this may fire more than once.
  onError?: (error: CheckoutError) => void;
  // Absent hides the header back arrow — used when the flow has no
  // meaningful earlier screen to return to (an authenticated item purchase).
  onBack?: () => void;
  onDismiss?: () => void;
}

// PaymentStep is the standalone payment screen: the shell chrome (title,
// identity note, back arrow, secure footer) around the shared PaymentForm,
// which owns the Payment Element mount and confirmation.
export function PaymentStep({
  product,
  price,
  amountCentsOverride,
  clientSecret,
  publishableKey,
  checkoutId,
  connectedAccount,
  domainId,
  maskedEmail,
  onSwitchIdentity,
  promotionCodesEnabled,
  savePaymentMethod,
  showExpressCheckout,
  walletsEnabled,
  onWalletsReady,
  onPromoChange,
  campaignPromo,
  onCampaignApply,
  onCampaignApplyFailed,
  onSuccess,
  onError,
  onBack,
  onDismiss,
}: Props) {
  useT();
  // The live session, fed up by PaymentForm so the order summary renders
  // Stripe's own totals (tax, discounts, Adaptive Pricing).
  const [session, setSession] = useState<StripeCheckoutSession | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The promo entry's sidebar mount point: it changes the order summary's
  // totals, so it belongs with them — the form portals it here (see
  // PaymentForm.promoTarget) instead of rendering it inside the card form.
  const [promoTarget, setPromoTarget] = useState<HTMLDivElement | null>(null);

  return (
    <CheckoutShell
      ariaLabel={t('paymentStep.eyebrow')}
      currentStep={2}
      sidebar={(
        <>
          <OrderSummary product={product} price={price} summary={session} amountCentsOverride={amountCentsOverride} />
          <div ref={setPromoTarget} />
        </>
      )}
      wide
      onBack={onBack}
      backDisabled={submitting}
      onDismiss={onDismiss}
    >
      <div class="sm-eyebrow">{t('paymentStep.eyebrow')}</div>
      <h2 class="sm-title">{t('paymentStep.title')}</h2>
      <p class="sm-subtitle">{t('paymentStep.subtitle')}</p>

      {maskedEmail && (
        <p class="sm-identity-note">
          {t('paywall.purchasingAs')} <strong>{maskedEmail}</strong>
          {onSwitchIdentity && (
            <>
              {' · '}
              <button type="button" class="sm-identity-change" onClick={onSwitchIdentity}>{t('paywall.notYou')}</button>
            </>
          )}
        </p>
      )}

      <PaymentForm
        clientSecret={clientSecret}
        publishableKey={publishableKey}
        checkoutId={checkoutId}
        connectedAccount={connectedAccount}
        product={product}
        price={price}
        domainId={domainId}
        promotionCodesEnabled={promotionCodesEnabled}
        savePaymentMethod={savePaymentMethod}
        promoTarget={promoTarget}
        showExpressCheckout={showExpressCheckout}
        walletsEnabled={walletsEnabled}
        onWalletsReady={onWalletsReady}
        onPromoChange={onPromoChange}
        campaignPromo={campaignPromo}
        onCampaignApply={onCampaignApply}
        onCampaignApplyFailed={onCampaignApplyFailed}
        onSessionChange={setSession}
        onSubmittingChange={setSubmitting}
        onSuccess={onSuccess}
        onError={onError}
      />

      <div class="sm-secure" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {t('common.securedByStripe')}
      </div>
    </CheckoutShell>
  );
}
